/**
 * ProfileIcon Component for LOOPINO AI Mobile
 * Shows company logo in mobile header
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useCampaign } from '../../CampaignProvider/CampaignProvider';
import useCompany from '../../../hooks/useCompany';
import avatarPlaceholder from '../../../assets/images/avater-placeholder.png';
import { API_CONFIG } from '../../../config/api';
import { migrateLegacyMediaUrlsInLocalStorage, normalizeMediaUrl } from '../../../utils/normalizeMediaUrl';

const ProfileIcon = ({ onProfileClick }) => {
  let activeCampaign = null;
  try {
    const campaignContext = useCampaign();
    activeCampaign = campaignContext?.activeCampaign || null;
  } catch (error) {
    if (typeof window !== 'undefined') {
      console.warn('[ProfileIcon] Rendered outside CampaignProvider. Active campaign data unavailable.');
    }
  }
  const { getCompanyDetails, currentCompany } = useCompany();
  const [companyData, setCompanyData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadedLogo, setUploadedLogo] = useState(null);
  const [logoTrigger, setLogoTrigger] = useState(0);
  const [loadedCompanyId, setLoadedCompanyId] = useState(null);
  const [logoObjectFit, setLogoObjectFit] = useState(() => {
    const saved = localStorage.getItem('companyLogoObjectFit');
    return saved || 'cover';
  });


  // Helper to resolve the correct logo URL from company data
  const resolveLogoUrl = useCallback((data) => {
    if (!data) return null;

    // Handle case where data is wrapped in a company property
    const companyData = data.company || data;
    const logo = companyData.logo;

    // If logo is an array, use the latest entry with a valid URL
    if (Array.isArray(logo) && logo.length > 0) {
      const latest = logo[logo.length - 1];
      if (latest && typeof latest === 'object') {
        if (latest.url) {
          return latest.url;
        }
        if (latest.preview) {
          return latest.preview;
        }
      }
    }

    // If logo is an object with url or preview
    if (logo && typeof logo === 'object' && !Array.isArray(logo)) {
      if (logo.url) {
        return logo.url;
      }
      if (logo.preview) {
        return logo.preview;
      }
    }

    // If logo is a direct string URL
    if (typeof logo === 'string') {
      return logo;
    }

    // Fallback: try brand_images if available
    if (Array.isArray(companyData.brand_images) && companyData.brand_images.length > 0) {
      const img = companyData.brand_images[0];
      if (img && img.url) return img.url;
      if (img && img.preview) return img.preview;
    }

    return null;
  }, []);

  // Load uploaded logo from localStorage on mount and listen for changes
  useEffect(() => {
    // Load initial logo from localStorage
    const loadUploadedLogo = () => {
      migrateLegacyMediaUrlsInLocalStorage(
        ['companyLogoPreview', 'companyLogoRemoteUrl'],
        API_CONFIG.BASE_URL
      );

      const logoPreview = normalizeMediaUrl(localStorage.getItem('companyLogoPreview'), API_CONFIG.BASE_URL);
      const logoName = localStorage.getItem('companyLogoName');

      if (logoPreview && logoName) {
        setUploadedLogo({ preview: logoPreview, name: logoName });
      } else {
        setUploadedLogo(null);
      }
    };

    // Load on mount
    loadUploadedLogo();

    // Listen for logo changes from MobileCompanyProfile
    const handleLogoChange = (event) => {
      const { preview, name } = event.detail;
      const normalizedPreview = normalizeMediaUrl(preview, API_CONFIG.BASE_URL);
      if (normalizedPreview && name) {
        setUploadedLogo({ preview: normalizedPreview, name });
      } else {
        setUploadedLogo(null);
      }
      // Trigger re-render to check for DOM changes
      setLogoTrigger(prev => prev + 1);
    };

    // Listen for logo object-fit changes from MobileCompanyProfile
    const handleLogoFitChange = (event) => {
      const { objectFit } = event.detail;
      setLogoObjectFit(objectFit);
    };

    // Listen for DOM changes (when logo gets loaded in the page)
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' || mutation.type === 'attributes') {
          const logoImg = document.querySelector('.mobile-company-logo-img');
          if (logoImg && logoImg.src && !logoImg.src.includes('data:image')) {
            setLogoTrigger(prev => prev + 1);
          }
        }
      });
    });

    // Observe the document for changes
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['src']
    });

    window.addEventListener('companyLogoChanged', handleLogoChange);
    window.addEventListener('companyLogoFitChanged', handleLogoFitChange);

    return () => {
      window.removeEventListener('companyLogoChanged', handleLogoChange);
      window.removeEventListener('companyLogoFitChanged', handleLogoFitChange);
      observer.disconnect();
    };
  }, []);

  // Sync logo from currentCompany (independent of campaigns)
  useEffect(() => {
    if (currentCompany) {
      const logoUrl = normalizeMediaUrl(resolveLogoUrl(currentCompany), API_CONFIG.BASE_URL);
      if (logoUrl && !logoUrl.includes('blob:') && !logoUrl.includes('placeholder')) {
        console.log('[ProfileIcon] Syncing logo from currentCompany:', logoUrl);
        localStorage.setItem('companyLogoPreview', logoUrl);
        localStorage.setItem('companyLogoName', currentCompany?.name || 'logo');
        if (currentCompany?.id) {
          localStorage.setItem('companyLogoCompanyId', currentCompany.id);
        } else {
          localStorage.removeItem('companyLogoCompanyId');
        }
        localStorage.setItem('companyLogoRemoteUrl', logoUrl);
        localStorage.removeItem('companyLogoPreviewUpdatedAt');
        setUploadedLogo({ preview: logoUrl, name: currentCompany?.name || 'logo' });
      } else {
        // IMPORTANT: If currentCompany has no logo, clean localStorage
        console.log('[ProfileIcon] CurrentCompany has no logo, cleaning localStorage');
        localStorage.removeItem('companyLogoPreview');
        localStorage.removeItem('companyLogoName');
        localStorage.removeItem('companyLogoCompanyId');
        localStorage.removeItem('companyLogoRemoteUrl');
        localStorage.removeItem('companyLogoPreviewUpdatedAt');
        setUploadedLogo(null);
      }
    } else {
      // IMPORTANT: If no currentCompany, clean localStorage
      console.log('[ProfileIcon] No currentCompany, cleaning localStorage');
      localStorage.removeItem('companyLogoPreview');
      localStorage.removeItem('companyLogoName');
      localStorage.removeItem('companyLogoCompanyId');
      localStorage.removeItem('companyLogoRemoteUrl');
      localStorage.removeItem('companyLogoPreviewUpdatedAt');
      setUploadedLogo(null);
    }
  }, [currentCompany, resolveLogoUrl]);

  // Load company data when active campaign changes
  useEffect(() => {
    const loadCompanyData = async () => {
      if (!activeCampaign) {
        // Don't clean localStorage - keep currentCompany logo
        setCompanyData(null);
        setLoadedCompanyId(null);
        return;
      }

      // Extract company ID - handle both string and object formats
      let companyId = null;
      if (typeof activeCampaign.company_id === 'string') {
        companyId = activeCampaign.company_id;
      } else if (typeof activeCampaign.company_id === 'object' && activeCampaign.company_id?.id) {
        companyId = activeCampaign.company_id.id;
      } else if (activeCampaign.company?.id) {
        companyId = activeCampaign.company.id;
      } else if (typeof activeCampaign.company === 'string') {
        companyId = activeCampaign.company;
      }

      if (!companyId) {
        // Don't clean localStorage - keep currentCompany logo
        setCompanyData(null);
        setLoadedCompanyId(null);
        return;
      }

      // Check if we already have the data for this company
      if (loadedCompanyId === companyId) {
        return;
      }

      setIsLoading(true);
      try {
        const company = await getCompanyDetails(companyId);

        setCompanyData(company);
        setLoadedCompanyId(companyId);

        // Sync logo to localStorage if available from backend
        const logoUrl = normalizeMediaUrl(resolveLogoUrl(company), API_CONFIG.BASE_URL);

        if (logoUrl && !logoUrl.includes('blob:') && !logoUrl.includes('placeholder')) {
          // Store backend logo in localStorage for persistence
          localStorage.setItem('companyLogoPreview', logoUrl);
          localStorage.setItem('companyLogoName', company?.name || 'logo');
          if (companyId) {
            localStorage.setItem('companyLogoCompanyId', companyId);
          } else {
            localStorage.removeItem('companyLogoCompanyId');
          }
          localStorage.setItem('companyLogoRemoteUrl', logoUrl);
          localStorage.removeItem('companyLogoPreviewUpdatedAt');
          setUploadedLogo({ preview: logoUrl, name: company?.name || 'logo' });
        } else {
          // IMPORTANT: If loaded company has no logo, clean localStorage
          console.log('[ProfileIcon] Loaded company has no logo, cleaning localStorage');
          localStorage.removeItem('companyLogoPreview');
          localStorage.removeItem('companyLogoName');
          localStorage.removeItem('companyLogoCompanyId');
          localStorage.removeItem('companyLogoRemoteUrl');
          localStorage.removeItem('companyLogoPreviewUpdatedAt');
          setUploadedLogo(null);
        }
      } catch (error) {
        console.error('[ProfileIcon] ❌ Error loading company data:', error);
        setCompanyData(null);
        setLoadedCompanyId(null);
      } finally {
        setIsLoading(false);
      }
    };

    loadCompanyData();
  }, [activeCampaign?.company_id, getCompanyDetails, resolveLogoUrl, loadedCompanyId]);

  const handleProfileClick = () => {
    if (onProfileClick) {
      onProfileClick(companyData || { name: 'Unknown Company' });
    }
  };

  // Get company logo or return null for empty placeholder
  const getLogoData = () => {
    if (isLoading) {
      return {
        src: null,
        name: 'Caricamento...',
        alt: 'Loading company logo',
        isLoading: true
      };
    }

    // First check backend logo
    const resolvedLogo = normalizeMediaUrl(resolveLogoUrl(companyData), API_CONFIG.BASE_URL);
    if (resolvedLogo) {
      return {
        src: resolvedLogo,
        name: companyData?.name || 'Azienda',
        alt: `${companyData?.name || 'Azienda'} Logo`
      };
    }

    // Check if there's a valid logo in the page
    const companyLogoImg = document.querySelector('.mobile-company-logo-img');
    if (companyLogoImg && companyLogoImg.src && !companyLogoImg.src.includes('data:image') && !companyLogoImg.src.includes('placeholder')) {
      return {
        src: normalizeMediaUrl(companyLogoImg.src, API_CONFIG.BASE_URL),
        name: companyData?.name || 'Azienda',
        alt: `${companyData?.name || 'Azienda'} Logo`
      };
    }

    // Check uploaded logo from localStorage (only if valid and not placeholder)
    if (uploadedLogo && uploadedLogo.preview && !uploadedLogo.preview.includes('placeholder')) {
      return {
        src: normalizeMediaUrl(uploadedLogo.preview, API_CONFIG.BASE_URL),
        name: companyData?.name || 'Azienda',
        alt: `${companyData?.name || 'Azienda'} Logo`
      };
    }

    // Clean localStorage if no valid logo exists
    if (!resolvedLogo && uploadedLogo) {
      localStorage.removeItem('companyLogoPreview');
      localStorage.removeItem('companyLogoName');
      setUploadedLogo(null);
    }

    // Return null src to show empty placeholder for new users
    return {
      src: null,
      name: companyData?.name || 'Azienda',
      alt: companyData ? `${companyData.name} (Logo non disponibile)` : 'Logo aziendale',
      isEmpty: true
    };
  };

  const { src: profileImageSrc, name: profileName, alt: altText, isEmpty } = getLogoData();

  return (
    <div
      className="mobile-header-icon mobile-profile-icon"
      onClick={handleProfileClick}
      role="button"
      tabIndex={0}
      aria-label={`Profile: ${profileName}`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleProfileClick();
        }
      }}
    >
      {profileImageSrc ? (
        <img
          src={profileImageSrc}
          alt={altText}
          className="mobile-profile-avatar"
          style={{ objectFit: logoObjectFit }}
          onError={(e) => {
            // If image fails to load, hide the img element
            e.target.style.display = 'none';
            // Add empty class to show placeholder
            e.target.parentElement.classList.add('empty-avatar');
          }}
        />
      ) : (
        <div
          className={`mobile-profile-avatar-empty ${isLoading ? 'loading' : ''}`}
          aria-label={altText}
        >
          {isLoading ? (
            <div className="avatar-spinner"></div>
          ) : (
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="avatar-icon"
            >
              <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="2"/>
              <path d="M6 21C6 17.6863 8.68629 15 12 15C15.3137 15 18 17.6863 18 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          )}
        </div>
      )}
    </div>
  );
};

export default ProfileIcon;
